import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  onSnapshot,
  DocumentData,
  QuerySnapshot
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable } from 'rxjs';
import { TenableScan } from '../tenable/models/tenable-scan';
import { CompanyService } from './company.service';
import { LoggingService } from './logging.service';

export interface ScanStatusChange {
  scanId: string;
  name: string;
  previousStatus: string;
  currentStatus: string;
  timestamp: Date;
  company: string;
}

/**
 * Service to monitor Firebase for scan status changes
 * Specifically designed to detect scan completions for desktop notifications
 *
 * This service:
 * - Watches the tenable_scan collection in Firebase
 * - Detects when scans change from any status to 'completed'
 * - Provides observables for real-time scan status monitoring
 * - Avoids circular dependencies by being standalone
 */
@Injectable({
  providedIn: 'root',
})
export class ScanStatusMonitorService {
  private firestore: Firestore;
  private loggingService: LoggingService;
  private companyService: CompanyService;

  private injector!: Injector;
  private scanStatusChanges$ = new BehaviorSubject<ScanStatusChange[]>([]);
  private lastKnownStatuses = new Map<string, string>();
  private isMonitoring = false;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    try {
      this.firestore = inject(Firestore);
      this.loggingService = inject(LoggingService);
      this.companyService = inject(CompanyService);
      this.injector = inject(Injector);

      if (!this.firestore) {
        console.error('❌ [ScanStatusMonitorService] Firestore injection failed');
        throw new Error('Firestore initialization failed');
      }
    } catch (error) {
      console.error('❌ [ScanStatusMonitorService] Failed to inject dependencies:', error);
      throw error;
    }
  }

  /**
   * Start monitoring Firebase for scan status changes
   * Returns observable of scan status changes
   */
  startMonitoring(): Observable<ScanStatusChange[]> {
    if (this.isMonitoring) {
      return this.scanStatusChanges$.asObservable();
    }

    // Get company context for filtering scans
    this.companyService.getCurrentUserCompany().subscribe((company: any) => {
      if (company && company.uid) {
        this.setupFirebaseListener(company.uid);
      } else {
        this.loggingService.logWarning(
          'No company context available for scan monitoring',
        );
      }
    });

    return this.scanStatusChanges$.asObservable();
  }

  /**
   * Stop monitoring Firebase
   */
  stopMonitoring(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    this.isMonitoring = false;
    this.lastKnownStatuses.clear();
    this.scanStatusChanges$.next([]);
  }

  /**
   * Get the current scan status changes observable
   */
  getScanStatusChanges(): Observable<ScanStatusChange[]> {
    return this.scanStatusChanges$.asObservable();
  }

  /**
   * Get last known status for a specific scan
   */
  getLastKnownStatus(scanId: string): string | null {
    return this.lastKnownStatuses.get(scanId) || null;
  }

  /**
   * Check if monitoring is currently active
   */
  isCurrentlyMonitoring(): boolean {
    return this.isMonitoring;
  }

  /**
   * Setup Firebase real-time listener for scan status changes
   */
  private setupFirebaseListener(currentCompanyId: string): void {
    try {
      // Validate inputs before proceeding
      if (!currentCompanyId) {
        this.loggingService.logWarning('setupFirebaseListener called with empty companyId');
        return;
      }

      // Validate Firestore is properly initialized (not just truthy)
      if (!this.firestore || typeof this.firestore !== 'object') {
        this.loggingService.logWarning('Firestore instance is not properly initialized for scan monitoring');
        return;
      }

      // Create query for company's scans
      const scansCollection = collection(this.firestore, 'tenable_scan');
      if (!scansCollection) {
        this.loggingService.logWarning('Failed to create scans collection reference');
        return;
      }

      const companyScansQuery = query(
        scansCollection,
        where('currentCompanyId', '==', currentCompanyId),
      );

      // Setup real-time listener (wrapped in injection context to prevent Angular warnings)
      this.unsubscribe = runInInjectionContext(this.injector, () =>
        onSnapshot(
          companyScansQuery,
          (snapshot: QuerySnapshot<DocumentData>) => {
            this.processSnapshotChanges(snapshot);
          },
          (error) => {
            this.loggingService.logError('Firebase listener error:', error);
            this.handleFirebaseError(error);
          },
        )
      );

      this.isMonitoring = true;
    } catch (error) {
      this.loggingService.logError('Failed to setup Firebase listener:', error);
      this.isMonitoring = false;
    }
  }

  /**
   * Process Firebase snapshot changes to detect status changes
   */
  private processSnapshotChanges(snapshot: QuerySnapshot<DocumentData>): void {
    const statusChanges: ScanStatusChange[] = [];

    snapshot.docs.forEach((doc) => {
      try {
        const scanData = doc.data() as TenableScan;
        const scanId =
          scanData.schedule_uuid || scanData.id?.toString() || doc.id;
        const currentStatus = scanData.status;
        const previousStatus = this.lastKnownStatuses.get(scanId);

        // Check if this is a status change
        if (previousStatus && previousStatus !== currentStatus) {
          const statusChange: ScanStatusChange = {
            scanId: scanId,
            name: scanData.name || 'Unknown Scan',
            previousStatus: previousStatus,
            currentStatus: currentStatus,
            timestamp: new Date(),
            company: scanData.currentCompanyId || 'Unknown',
          };

          statusChanges.push(statusChange);
        }

        // Update our status tracking
        this.lastKnownStatuses.set(scanId, currentStatus);
      } catch (error) {
        this.loggingService.logError('Error processing scan document:', {
          error,
          docId: doc.id,
        });
      }
    });

    // Emit status changes if any were detected
    if (statusChanges.length > 0) {
      this.scanStatusChanges$.next(statusChanges);
    }
  }

  /**
   * Handle Firebase errors
   */
  private handleFirebaseError(error: any): void {
    this.loggingService.logError('Firebase monitoring error:', error);

    // Try to restart monitoring after a delay
    setTimeout(() => {
      this.stopMonitoring();
      // Will restart when company$ emits again
    }, 5000);
  }

  /**
   * Get completion status changes only
   */
  getCompletionChanges(): Observable<ScanStatusChange[]> {
    return new Observable((subscriber) => {
      this.getScanStatusChanges().subscribe((changes) => {
        const completions = changes.filter(
          (change) => change.currentStatus === 'completed',
        );
        if (completions.length > 0) {
          subscriber.next(completions);
        }
      });
    });
  }

  /**
   * Initialize with existing scan statuses (called once on service start)
   */
  initializeWithExistingScans(): void {
    this.companyService.getCurrentUserCompany().subscribe((company: any) => {
      if (company && company.uid) {
        // This will be populated as the Firebase listener processes initial data
      }
    });
  }
}
